import { FaBookOpen } from 'react-icons/fa';
import { ListItemIcon, ListItemText, Menu, MenuItem, makeStyles } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { useRouter } from 'next/router';
import {
  getBrochureRequest,
  resetBrochureState,
  setIsViewingBrochure,
  updateBrochureFields,
} from '../../../store/slices/brochure';
import BrochureEmailModal from './BrochureEmailModal';
import Button from '../../Button';
import { SearchStatus } from '../../../constants';

const useStyles = makeStyles(() => ({
  menu: {
    '& .MuiListItemText-primary': {
      fontFamily: 'var(--fontFamilyBody)',
    },
  },
}));

const BrochureActions = () => {
  const classes = useStyles();
  const router = useRouter();
  const { brochureId: queryBrochureId } = router.query;
  const { brochure, getBrochureStatus, brochures, isViewingBrochure } = useSelector(
    (state) => state.brochure,
  );
  const brochureById = useSelector((state) => state.brochure.brochures[queryBrochureId]);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [doShowMenu, setDoShowMenu] = useState(null);
  const [doShowEmailModal, setDoShowEmailModal] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const { track } = useAnalytics();
  const { travelAgency } = useSelector((state) => state.user);
  const brochureSettings =
    (travelAgency && travelAgency.settings && travelAgency.settings.brochureSettings) || {};
  const displayLabel = (brochureSettings.DisplayName || 'brochure').toLowerCase();
  const { sendBrochureStatus } = brochure;

  useEffect(() => {
    setDoShowMenu(
      brochure &&
        Object.keys(brochure.trips).length &&
        _.find(Object.values(brochure.trips), (t) => t.tripSegments && t.tripSegments.length > 0) !=
          null,
    );
  }, [brochure]);

  function closeDropdown() {
    setAnchorEl(null);
  }

  function showDropdown(event) {
    setAnchorEl(event.currentTarget);
  }

  const onDropdownClick = (event) => {
    track('BrochureActions.onDropdownClick', {
      isOpen: anchorEl === null,
    });
    if (anchorEl === null) {
      showDropdown(event);
    } else {
      closeDropdown();
    }
  };

  const onSendEmailClick = () => {
    closeDropdown();
    setDoShowEmailModal(true);
    dispatch(setIsViewingBrochure(true));
    track('BrochureActions.onPreviewAndSendEmailClick');
  };

  const resetBrochureStateAndUrlId = () => {
    dispatch(resetBrochureState());
    const { pathname, query } = router;
    const newQuery = { ...query };
    delete newQuery.brochureId;
    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const handleCloseModal = () => {
    setDoShowEmailModal(false);
    dispatch(setIsViewingBrochure(false));
    track('BrochureActions.handleCloseModal');

    if (sendBrochureStatus === SearchStatus.DONE) {
      resetBrochureStateAndUrlId();
    }
  };

  useEffect(() => {
    if (!isViewingBrochure && doShowEmailModal) {
      setDoShowEmailModal(false);
    }
  }, [doShowEmailModal, isViewingBrochure]);

  const onResetClick = () => {
    closeDropdown();
    resetBrochureStateAndUrlId();
    track('BrochureActions.onResetClick');
  };

  useEffect(() => {
    if (queryBrochureId) {
      dispatch(getBrochureRequest({ id: queryBrochureId }));
    }
  }, [queryBrochureId, dispatch]);

  useEffect(() => {
    if (queryBrochureId && brochureById && !hasSaved) {
      dispatch(
        updateBrochureFields({
          brochureId: queryBrochureId,
          fields: {
            ...((brochures && brochures[queryBrochureId]) || {}),
          },
        }),
      );
      setHasSaved(true);
    }
  }, [brochureById, queryBrochureId, hasSaved, dispatch, brochures, travelAgency]);

  useEffect(() => {
    if (queryBrochureId && brochureById) {
      setDoShowMenu(!!queryBrochureId);
      dispatch(setIsViewingBrochure(doShowEmailModal));
    }
  }, [brochureById, dispatch, queryBrochureId, doShowEmailModal]);

  // TODO: refactor to use our dropdown element?
  const renderComponent = () => (
    <>
      <Button
        onClick={onDropdownClick}
        variant={Button.buttonVariants.OUTLINE_WHITE_BG}
        className="py-2 px-3 text-base"
        disabled={getBrochureStatus === SearchStatus.SEARCHING}
      >
        <>
          <FaBookOpen className="inline -mt-1" />{' '}
          {queryBrochureId && getBrochureStatus === SearchStatus.SEARCHING ? 'Loading' : 'Your'}{' '}
          {displayLabel}{' '}
          {anchorEl ? (
            <ArrowDropUpIcon className="opacity-50" />
          ) : (
            <ArrowDropDownIcon className="opacity-50" />
          )}
        </>
      </Button>
      <Menu
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id="customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={onDropdownClick}
        classes={{ paper: classes.menu }}
      >
        <MenuItem component="li" onClick={onSendEmailClick}>
          <ListItemIcon>
            <MailOutlineIcon />
          </ListItemIcon>
          <ListItemText primary="Preview and share" />
        </MenuItem>
        <MenuItem component="li" onClick={onResetClick}>
          <ListItemIcon>
            <DeleteOutlineOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Reset" />
        </MenuItem>
      </Menu>
      <BrochureEmailModal
        isModalOpen={doShowEmailModal}
        handleCloseModal={handleCloseModal}
        id={queryBrochureId}
      />
    </>
  );

  return doShowMenu ? renderComponent() : '';
};

export default BrochureActions;
